/**
 * Plugin Crash Recovery — Wave 22.13.9
 *
 * Monitors plugin crash frequency and automatically disables plugins
 * that exceed the crash threshold. Provides teardown execution,
 * crash badge display in DevTools, and user-initiated re-enable.
 *
 * Integration points:
 *   - PluginStabilityStore (crash counting)
 *   - PluginLogStore (crash diagnostics)
 *   - PluginEventBus (crash events)
 *   - PluginLoader (teardown + disable)
 *   - DevTools (crash badge + re-enable)
 */

import { usePluginStabilityStore, CRASH_THRESHOLD } from './plugin-stability-store';
import { usePluginLogStore } from './plugin-log-store';
import { usePluginEventBusStore } from '../plugin-event-bus';

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export interface CrashRecoveryResult {
  pluginId: string;
  disabled: boolean;
  crashCount: number;
  teardownExecuted: boolean;
  error?: string;
}

// ────────────────────────────────────────────────────────
// Teardown registry
// ────────────────────────────────────────────────────────

const teardownHandlers = new Map<string, () => void | Promise<void>>();

/**
 * Register a teardown handler for a plugin.
 * Called during plugin loading so we can clean up on crash.
 */
export function registerTeardownHandler(
  pluginId: string,
  handler: () => void | Promise<void>,
): void {
  teardownHandlers.set(pluginId, handler);
}

/**
 * Unregister a teardown handler.
 */
export function unregisterTeardownHandler(pluginId: string): void {
  teardownHandlers.delete(pluginId);
}

// ────────────────────────────────────────────────────────
// Crash recovery
// ────────────────────────────────────────────────────────

/**
 * Handle a plugin crash. Records the crash, runs teardown if needed,
 * and disables the plugin if the crash threshold is exceeded.
 */
export async function handlePluginCrash(
  pluginId: string,
  error: Error | string,
): Promise<CrashRecoveryResult> {
  const errorMessage = error instanceof Error ? error.message : error;
  const stabilityStore = usePluginStabilityStore.getState();
  const logStore = usePluginLogStore.getState();

  // Record crash
  const shouldDisable = stabilityStore.recordCrash(
    pluginId,
    `Plugin crash: ${errorMessage}`,
    { stack: error instanceof Error ? error.stack : undefined },
  );

  logStore.appendLog(pluginId, 'error', `CRASH: ${errorMessage}`, 'crash-recovery');

  const record = stabilityStore.getRecord(pluginId);
  let teardownExecuted = false;

  if (shouldDisable) {
    logStore.appendLog(
      pluginId,
      'error',
      `Plugin disabled by crash recovery (${record.crashCount}/${CRASH_THRESHOLD} crashes)`,
      'crash-recovery',
    );

    // Run teardown
    const teardown = teardownHandlers.get(pluginId);
    if (teardown) {
      try {
        await teardown();
        teardownExecuted = true;
        logStore.appendLog(pluginId, 'info', 'Teardown completed', 'crash-recovery');
      } catch (teardownError) {
        const teardownMsg = teardownError instanceof Error ? teardownError.message : String(teardownError);
        logStore.appendLog(pluginId, 'error', `Teardown failed: ${teardownMsg}`, 'crash-recovery');
      }
    }

    // Emit crash event (best-effort)
    try {
      usePluginEventBusStore.getState().emit('system', 'plugin.crashed', {
        pluginId,
        crashCount: record.crashCount,
        disabled: true,
        timestamp: new Date().toISOString(),
      });
    } catch {
      /* best-effort */
    }
  }

  return {
    pluginId,
    disabled: shouldDisable,
    crashCount: record.crashCount,
    teardownExecuted,
  };
}

/**
 * Re-enable a plugin that was disabled by crash recovery.
 * Resets crash count and allows the plugin to function again.
 */
export function reEnablePlugin(pluginId: string): boolean {
  const stabilityStore = usePluginStabilityStore.getState();
  const record = stabilityStore.getRecord(pluginId);

  if (!record.disabledByCrashRecovery) {
    return false;
  }

  stabilityStore.reEnablePlugin(pluginId);

  const logStore = usePluginLogStore.getState();
  logStore.appendLog(pluginId, 'info', 'Plugin re-enabled by user', 'crash-recovery');

  try {
    usePluginEventBusStore.getState().emit('system', 'plugin.reEnabled', {
      pluginId,
      timestamp: new Date().toISOString(),
    });
  } catch {
    /* best-effort */
  }

  return true;
}

/**
 * Check if a plugin is currently disabled by crash recovery.
 */
export function isPluginDisabledByCrash(pluginId: string): boolean {
  return usePluginStabilityStore.getState().getRecord(pluginId).disabledByCrashRecovery;
}

/**
 * Get crash recovery info for DevTools.
 */
export function getCrashRecoveryInfo(pluginId: string): {
  crashCount: number;
  disabled: boolean;
  lastCrashTimestamp: string | null;
  threshold: number;
} {
  const record = usePluginStabilityStore.getState().getRecord(pluginId);
  return {
    crashCount: record.crashCount,
    disabled: record.disabledByCrashRecovery,
    lastCrashTimestamp: record.lastCrashTimestamp,
    threshold: CRASH_THRESHOLD,
  };
}
